/**
 * File: assets/basari-lens-addons.js
 * Purpose: Lens Add-ons Selection Framework — controller / service / state.
 * Sprint: 1B.14 Phase A — Architecture & Navigation (no UI / no business logic)
 * Owner: Basari Theme / PDP
 * Dependencies: none (drawer consumes via window.BasariLensAddons)
 *
 * Architecture:
 *   Drawer → Router → LensAddonsController → LensAddonsService
 *     → LensAddonsState (lensFlow.lensAddons)
 *     → Future Price Calculator (extension point)
 *     → Future Recommendation Engine (placeholder fields only)
 *     → Future VisionCarePro / Cart integration layers
 *
 * Out of scope (Phase B+):
 *   - Checkbox / expandable card / tint / index UI
 *   - Running total presentation
 *   - Price calculations / recommendation algorithm
 *   - Validation / cart / VisionCarePro / payload generation
 */
(function (global) {
  'use strict';

  var BasariLensAddons = global.BasariLensAddons || {};

  /* ---------------------------------------------------------------------------
   * Events — UI binds in Phase B; architecture emits now
   * ------------------------------------------------------------------------- */
  var EVENTS = {
    INITIALIZED: 'basari:lens-addons:initialized',
    OPENED: 'basari:lens-addons:opened',
    CLOSED: 'basari:lens-addons:closed',
    UPDATED: 'basari:lens-addons:updated',
    RESET: 'basari:lens-addons:reset',
    DESTROYED: 'basari:lens-addons:destroyed',
    NAVIGATION_REQUESTED: 'basari:lens-addons:navigation-requested',
    SELECTION_PREPARED: 'basari:lens-addons:selection-prepared',
    PRICE_REQUESTED: 'basari:lens-addons:price-requested',
    RECOMMENDATION_REQUESTED: 'basari:lens-addons:recommendation-requested'
  };

  var LOADING_STATUS = {
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error'
  };

  var ADDON_IDS = {
    TRANSPARENT: 'transparent',
    THIN_LENS: 'thin-lens',
    TINTED: 'tinted',
    PHOTOCHROMIC: 'photochromic',
    BLUE_FILTER: 'blue-filter',
    POLARISED: 'polarised'
  };

  var ROUTE_ID = 'lens-addons';

  function emitSafe(emitter, name, detail) {
    if (typeof emitter !== 'function') return;
    try {
      emitter(name, detail || {});
    } catch (error) {
      /* Host emit failures must not break lens-addons flow. */
    }
  }

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function shallowClone(value) {
    if (Array.isArray(value)) return value.slice();
    if (!isObject(value)) return value;
    var next = {};
    var key;
    for (key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        next[key] = value[key];
      }
    }
    return next;
  }

  function assign(target) {
    var result = target || {};
    var i;
    var source;
    var key;
    for (i = 1; i < arguments.length; i += 1) {
      source = arguments[i];
      if (!isObject(source)) continue;
      for (key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          result[key] = source[key];
        }
      }
    }
    return result;
  }

  function normalizeLoadingState(value) {
    var key = String(value || '')
      .toLowerCase()
      .trim();
    if (
      key === LOADING_STATUS.IDLE ||
      key === LOADING_STATUS.LOADING ||
      key === LOADING_STATUS.READY ||
      key === LOADING_STATUS.ERROR
    ) {
      return key;
    }
    return LOADING_STATUS.IDLE;
  }

  /* ---------------------------------------------------------------------------
   * Accessibility foundation — architecture hooks only (no UI behaviour)
   * ------------------------------------------------------------------------- */
  function createAccessibilityState(overrides) {
    overrides = isObject(overrides) ? overrides : {};
    return {
      /* Focus management — Phase B wires trap / restore */
      focusReturnSelector: overrides.focusReturnSelector != null ? String(overrides.focusReturnSelector) : null,
      initialFocusSelector:
        overrides.initialFocusSelector != null ? String(overrides.initialFocusSelector) : null,
      /* Keyboard navigation — Phase B binds key handlers */
      keyboardNavigationEnabled: overrides.keyboardNavigationEnabled !== false,
      /* ARIA / screen reader — Phase B binds live regions */
      ariaLivePolite: overrides.ariaLivePolite != null ? String(overrides.ariaLivePolite) : '',
      labelledBy: overrides.labelledBy != null ? String(overrides.labelledBy) : null,
      describedBy: overrides.describedBy != null ? String(overrides.describedBy) : null
    };
  }

  /* ---------------------------------------------------------------------------
   * Recommendation placeholder — no algorithm (Phase D / VisionCarePro)
   * ------------------------------------------------------------------------- */
  function createRecommendationState(overrides) {
    overrides = isObject(overrides) ? overrides : {};
    return {
      recommendedOptions: Array.isArray(overrides.recommendedOptions)
        ? overrides.recommendedOptions.slice()
        : [],
      recommendedLensIndex:
        overrides.recommendedLensIndex != null ? String(overrides.recommendedLensIndex) : null,
      recommendationReason:
        overrides.recommendationReason != null ? String(overrides.recommendationReason) : null
    };
  }

  /* ---------------------------------------------------------------------------
   * LensAddonsState — selection + UI prep + pricing / recommendation placeholders
   * ------------------------------------------------------------------------- */
  function createLensAddonsState(overrides) {
    overrides = isObject(overrides) ? overrides : {};

    var recommendation =
      overrides.recommendationState != null
        ? createRecommendationState(overrides.recommendationState)
        : createRecommendationState({
            recommendedOptions: overrides.recommendedOptions,
            recommendedLensIndex: overrides.recommendedLensIndex,
            recommendationReason: overrides.recommendationReason
          });

    return {
      selectedOptions: Array.isArray(overrides.selectedOptions) ? overrides.selectedOptions.slice() : [],
      expandedCards: Array.isArray(overrides.expandedCards) ? overrides.expandedCards.slice() : [],
      selectedTintColor: overrides.selectedTintColor != null ? String(overrides.selectedTintColor) : null,
      selectedLensIndex: overrides.selectedLensIndex != null ? String(overrides.selectedLensIndex) : null,
      /* Placeholder cents — Phase C Price Calculator owns real totals */
      totalPrice: overrides.totalPrice != null ? Number(overrides.totalPrice) || 0 : 0,
      recommendationState: recommendation,
      /* Flat aliases kept for Task 8 field names */
      recommendedOptions: recommendation.recommendedOptions.slice(),
      recommendedLensIndex: recommendation.recommendedLensIndex,
      recommendationReason: recommendation.recommendationReason,
      loadingState: normalizeLoadingState(overrides.loadingState),
      accessibility: createAccessibilityState(overrides.accessibility),
      meta: isObject(overrides.meta) ? shallowClone(overrides.meta) : {},
      updatedAt: overrides.updatedAt != null ? overrides.updatedAt : null
    };
  }

  function cloneLensAddonsState(state) {
    return createLensAddonsState(state || {});
  }

  function mergeLensAddonsState(base, patch) {
    var next = cloneLensAddonsState(base);
    if (!isObject(patch)) return next;

    if (Array.isArray(patch.selectedOptions)) next.selectedOptions = patch.selectedOptions.slice();
    if (Array.isArray(patch.expandedCards)) next.expandedCards = patch.expandedCards.slice();
    if (patch.selectedTintColor !== undefined) {
      next.selectedTintColor = patch.selectedTintColor != null ? String(patch.selectedTintColor) : null;
    }
    if (patch.selectedLensIndex !== undefined) {
      next.selectedLensIndex = patch.selectedLensIndex != null ? String(patch.selectedLensIndex) : null;
    }
    if (patch.totalPrice != null) next.totalPrice = Number(patch.totalPrice) || 0;
    if (patch.loadingState != null) next.loadingState = normalizeLoadingState(patch.loadingState);
    if (isObject(patch.meta)) next.meta = assign({}, next.meta, patch.meta);

    if (
      isObject(patch.recommendationState) ||
      patch.recommendedOptions !== undefined ||
      patch.recommendedLensIndex !== undefined ||
      patch.recommendationReason !== undefined
    ) {
      next.recommendationState = createRecommendationState(
        assign({}, next.recommendationState, patch.recommendationState || {}, {
          recommendedOptions:
            patch.recommendedOptions !== undefined
              ? patch.recommendedOptions
              : (patch.recommendationState && patch.recommendationState.recommendedOptions) ||
                next.recommendationState.recommendedOptions,
          recommendedLensIndex:
            patch.recommendedLensIndex !== undefined
              ? patch.recommendedLensIndex
              : (patch.recommendationState && patch.recommendationState.recommendedLensIndex) ||
                next.recommendationState.recommendedLensIndex,
          recommendationReason:
            patch.recommendationReason !== undefined
              ? patch.recommendationReason
              : (patch.recommendationState && patch.recommendationState.recommendationReason) ||
                next.recommendationState.recommendationReason
        })
      );
      next.recommendedOptions = next.recommendationState.recommendedOptions.slice();
      next.recommendedLensIndex = next.recommendationState.recommendedLensIndex;
      next.recommendationReason = next.recommendationState.recommendationReason;
    }

    if (isObject(patch.accessibility)) {
      next.accessibility = createAccessibilityState(assign({}, next.accessibility, patch.accessibility));
    }

    next.updatedAt = patch.updatedAt != null ? patch.updatedAt : Date.now();
    return next;
  }

  /* ---------------------------------------------------------------------------
   * Static configuration — merchant Theme Settings can override labels later
   * ------------------------------------------------------------------------- */
  function createDefaultConfiguration(overrides) {
    overrides = isObject(overrides) ? overrides : {};

    var defaultAddons = [
      {
        id: ADDON_IDS.TRANSPARENT,
        title: 'Transparent (Clear lens)',
        description: '',
        price: 59900,
        expandable: false,
        hasTintColors: false,
        hasLensIndex: false
      },
      {
        id: ADDON_IDS.THIN_LENS,
        title: 'Thin Lens option',
        description: 'Refresh Your Looks with a special lens tint color of your choice',
        price: 59900,
        expandable: true,
        hasTintColors: false,
        hasLensIndex: true
      },
      {
        id: ADDON_IDS.TINTED,
        title: 'Tinted Effect',
        description: 'Refresh Your Looks with a special lens tint color of your choice',
        price: 59900,
        expandable: true,
        hasTintColors: true,
        hasLensIndex: false
      },
      {
        id: ADDON_IDS.PHOTOCHROMIC,
        title: 'PhotoChromic Effect',
        description: '',
        price: 59900,
        expandable: false,
        hasTintColors: false,
        hasLensIndex: false
      },
      {
        id: ADDON_IDS.BLUE_FILTER,
        title: 'Blue filter Option',
        description: '',
        price: 59900,
        expandable: false,
        hasTintColors: false,
        hasLensIndex: false
      },
      {
        id: ADDON_IDS.POLARISED,
        title: 'Polarised lens',
        description: '',
        price: 59900,
        expandable: false,
        hasTintColors: false,
        hasLensIndex: false
      }
    ];

    var defaultTintColors = [
      { id: 'yellow', label: 'Yellow', value: '#F5C518' },
      { id: 'orange', label: 'Orange', value: '#E67E22' },
      { id: 'pink', label: 'Pink', value: '#E91E8C' },
      { id: 'blue', label: 'Blue', value: '#2E86DE' },
      { id: 'navy', label: 'Navy', value: '#1B3A6B' },
      { id: 'teal', label: 'Teal', value: '#16A085' },
      { id: 'dark-green', label: 'Dark Green', value: '#1E5631' },
      { id: 'cyan', label: 'Cyan', value: '#00BCD4' }
    ];

    var defaultLensIndexList = [
      { id: '1.61', label: '1.61', value: '1.61' },
      { id: '1.67', label: '1.67', value: '1.67' },
      { id: '1.74', label: '1.74', value: '1.74' }
    ];

    return {
      availableAddons: Array.isArray(overrides.availableAddons)
        ? overrides.availableAddons.slice()
        : defaultAddons,
      defaultPricing: isObject(overrides.defaultPricing)
        ? shallowClone(overrides.defaultPricing)
        : { currency: 'INR', unit: 'cents' },
      tintColors: Array.isArray(overrides.tintColors) ? overrides.tintColors.slice() : defaultTintColors,
      lensIndexList: Array.isArray(overrides.lensIndexList)
        ? overrides.lensIndexList.slice()
        : defaultLensIndexList,
      /* Theme-facing copy — Liquid / Theme Editor override via drawer data attrs */
      labels: assign(
        {
          drawerTitle: 'Select Lens Options',
          confirmLabel: 'Confirm',
          closeLabel: 'Close',
          totalLabel: 'Total',
          recommendedLabel: 'Recommended',
          tintColorsLabel: 'Shade Colors',
          lensIndexLabel: 'Categories'
        },
        overrides.labels || {}
      ),
      featureToggles: assign(
        {
          enableTintSelector: true,
          enableLensIndexSelector: true,
          enableRecommendations: false,
          enablePricing: false
        },
        overrides.featureToggles || {}
      )
    };
  }

  /* ---------------------------------------------------------------------------
   * LensAddonsService — static config + extension points (no APIs / no calc)
   * ------------------------------------------------------------------------- */
  function LensAddonsService(options) {
    options = options || {};
    this.emit = options.emit || null;
    this.stateRef = options.stateRef || null;
    this.stateKey = options.stateKey || 'lensAddons';
    this.configuration = createDefaultConfiguration(options.configuration);
    /**
     * Future Price Calculator extension point.
     * Phase C: assign an object with calculate(state, configuration) → number.
     */
    this.priceCalculator = options.priceCalculator || null;
  }

  LensAddonsService.prototype._readState = function () {
    if (this.stateRef && this.stateKey && isObject(this.stateRef[this.stateKey])) {
      return this.stateRef[this.stateKey];
    }
    return createLensAddonsState();
  };

  LensAddonsService.prototype._writeState = function (state) {
    if (this.stateRef && this.stateKey) {
      this.stateRef[this.stateKey] = state;
    }
    return state;
  };

  LensAddonsService.prototype._commit = function (patch, eventName, detail) {
    var next = mergeLensAddonsState(this._readState(), patch);
    this._writeState(next);
    emitSafe(this.emit, eventName || EVENTS.UPDATED, assign({ state: cloneLensAddonsState(next) }, detail || {}));
    return next;
  };

  LensAddonsService.prototype.getConfiguration = function () {
    return createDefaultConfiguration(this.configuration);
  };

  LensAddonsService.prototype.getAvailableAddons = function () {
    return this.getConfiguration().availableAddons.slice();
  };

  LensAddonsService.prototype.getDefaultPricing = function () {
    return shallowClone(this.getConfiguration().defaultPricing);
  };

  LensAddonsService.prototype.getTintColors = function () {
    return this.getConfiguration().tintColors.slice();
  };

  LensAddonsService.prototype.getLensIndexList = function () {
    return this.getConfiguration().lensIndexList.slice();
  };

  LensAddonsService.prototype.getLabels = function () {
    return shallowClone(this.getConfiguration().labels);
  };

  LensAddonsService.prototype.getFeatureToggles = function () {
    return shallowClone(this.getConfiguration().featureToggles);
  };

  /**
   * Extension point — Phase C wires a real calculator.
   * @param {{ calculate: Function }|null} calculator
   */
  LensAddonsService.prototype.setPriceCalculator = function (calculator) {
    this.priceCalculator = calculator || null;
    return this.priceCalculator;
  };

  LensAddonsService.prototype.getPriceCalculator = function () {
    return this.priceCalculator;
  };

  /**
   * Pricing architecture stub — does not calculate business totals.
   * Returns existing state.totalPrice unless a future calculator is attached.
   */
  LensAddonsService.prototype.calculateTotal = function (state) {
    var current = state || this._readState();
    emitSafe(this.emit, EVENTS.PRICE_REQUESTED, {
      state: cloneLensAddonsState(current),
      hasCalculator: !!(this.priceCalculator && typeof this.priceCalculator.calculate === 'function')
    });

    if (this.priceCalculator && typeof this.priceCalculator.calculate === 'function') {
      return Number(this.priceCalculator.calculate(current, this.getConfiguration())) || 0;
    }

    return current && current.totalPrice != null ? Number(current.totalPrice) || 0 : 0;
  };

  /**
   * Recommendation architecture stub — no algorithm.
   * Returns existing placeholder recommendation fields.
   */
  LensAddonsService.prototype.getRecommendationPlaceholder = function () {
    var state = this._readState();
    emitSafe(this.emit, EVENTS.RECOMMENDATION_REQUESTED, {
      state: cloneLensAddonsState(state)
    });
    return createRecommendationState(state.recommendationState);
  };

  LensAddonsService.prototype.initialize = function (seed) {
    var state = createLensAddonsState(
      assign({}, seed || {}, {
        loadingState: LOADING_STATUS.READY,
        updatedAt: Date.now()
      })
    );
    this._writeState(state);
    emitSafe(this.emit, EVENTS.INITIALIZED, {
      state: cloneLensAddonsState(state),
      configuration: this.getConfiguration()
    });
    return state;
  };

  LensAddonsService.prototype.exportState = function () {
    return cloneLensAddonsState(this._readState());
  };

  LensAddonsService.prototype.update = function (patch) {
    return this._commit(patch, EVENTS.UPDATED);
  };

  /**
   * Prepare selection snapshot for future cart / payload layers.
   * No business rules — structural export only.
   */
  LensAddonsService.prototype.prepareSelection = function () {
    var state = this.exportState();
    var payload = {
      selectedOptions: state.selectedOptions.slice(),
      expandedCards: state.expandedCards.slice(),
      selectedTintColor: state.selectedTintColor,
      selectedLensIndex: state.selectedLensIndex,
      totalPrice: state.totalPrice,
      recommendationState: createRecommendationState(state.recommendationState),
      preparedAt: Date.now()
    };
    emitSafe(this.emit, EVENTS.SELECTION_PREPARED, { selection: payload });
    return payload;
  };

  LensAddonsService.prototype.reset = function () {
    var state = createLensAddonsState({
      loadingState: LOADING_STATUS.READY,
      updatedAt: Date.now()
    });
    this._writeState(state);
    emitSafe(this.emit, EVENTS.RESET, { state: cloneLensAddonsState(state) });
    return state;
  };

  /* ---------------------------------------------------------------------------
   * LensAddonsController — orchestration only (no business rules)
   * ------------------------------------------------------------------------- */
  function LensAddonsController(options) {
    options = options || {};
    this.service = options.service;
    this.navigate = typeof options.navigate === 'function' ? options.navigate : null;
    this.goBack = typeof options.goBack === 'function' ? options.goBack : null;
    this.closeDrawer = typeof options.closeDrawer === 'function' ? options.closeDrawer : null;
    this.emit = options.emit || null;
    this.initialized = false;
    this.isOpen = false;
  }

  LensAddonsController.prototype.initialize = function (seed) {
    if (this.initialized) {
      return this.service.exportState();
    }
    var state = this.service.initialize(seed);
    this.initialized = true;
    return state;
  };

  /**
   * Drawer lifecycle — open selection surface (architecture hook).
   * Does not own DOM; host drawer shows the step panel.
   */
  LensAddonsController.prototype.open = function (options) {
    options = options || {};
    this.initialize(options.seed);
    this.isOpen = true;
    this.service.update({
      loadingState: LOADING_STATUS.READY,
      accessibility: options.accessibility
    });
    emitSafe(this.emit, EVENTS.OPENED, {
      state: this.getState(),
      route: ROUTE_ID
    });
    return this.getState();
  };

  /**
   * Drawer lifecycle — close selection surface without destroying the stack.
   */
  LensAddonsController.prototype.close = function (options) {
    options = options || {};
    this.isOpen = false;
    emitSafe(this.emit, EVENTS.CLOSED, {
      state: this.getState(),
      route: ROUTE_ID
    });

    if (options.closeDrawer === true && typeof this.closeDrawer === 'function') {
      this.closeDrawer();
      return this.getState();
    }

    if (options.navigateBack === true) {
      this.requestBack(options.navigationOptions);
    }

    return this.getState();
  };

  LensAddonsController.prototype.reset = function () {
    if (!this.initialized) {
      return this.initialize();
    }
    return this.service.reset();
  };

  /**
   * Tear down controller flags. Host may discard the instance after destroy.
   */
  LensAddonsController.prototype.destroy = function () {
    this.isOpen = false;
    this.initialized = false;
    emitSafe(this.emit, EVENTS.DESTROYED, { route: ROUTE_ID });
    return true;
  };

  LensAddonsController.prototype.getState = function () {
    this.initialize();
    return this.service.exportState();
  };

  LensAddonsController.prototype.updateState = function (patch) {
    this.initialize();
    return this.service.update(patch);
  };

  LensAddonsController.prototype.prepareSelectionState = function () {
    this.initialize();
    return this.service.prepareSelection();
  };

  /**
   * Pricing architecture hook — Controller → Service → Future Price Calculator.
   * No calculation rules in Phase A.
   */
  LensAddonsController.prototype.requestTotalPrice = function () {
    this.initialize();
    return this.service.calculateTotal(this.getState());
  };

  /**
   * Recommendation architecture hook — returns placeholders only.
   */
  LensAddonsController.prototype.requestRecommendationPlaceholder = function () {
    this.initialize();
    return this.service.getRecommendationPlaceholder();
  };

  LensAddonsController.prototype.getConfiguration = function () {
    this.initialize();
    return this.service.getConfiguration();
  };

  LensAddonsController.prototype.getAccessibilityState = function () {
    return this.getState().accessibility;
  };

  /**
   * Request navigation through the host drawer router.
   * Controller never owns routing — it only asks.
   */
  LensAddonsController.prototype.requestNavigation = function (destination, options) {
    var next = String(destination || '').trim();
    options = options || {};

    emitSafe(this.emit, EVENTS.NAVIGATION_REQUESTED, {
      destination: next || null,
      options: options
    });

    if (next && this.navigate) {
      this.navigate(next, options);
    }

    return !!next;
  };

  LensAddonsController.prototype.requestBack = function (options) {
    options = options || {};
    emitSafe(this.emit, EVENTS.NAVIGATION_REQUESTED, {
      destination: 'back',
      options: options
    });
    if (typeof this.goBack === 'function') {
      this.goBack();
      return true;
    }
    return false;
  };

  /**
   * Wire a full stack for a drawer instance.
   */
  function createFramework(options) {
    options = options || {};

    var service = new LensAddonsService({
      emit: options.emit,
      stateRef: options.stateRef,
      stateKey: options.stateKey || 'lensAddons',
      configuration: options.configuration,
      priceCalculator: options.priceCalculator || null
    });

    var controller = new LensAddonsController({
      service: service,
      navigate: options.navigate,
      goBack: options.goBack,
      closeDrawer: options.closeDrawer,
      emit: options.emit
    });

    return {
      service: service,
      controller: controller
    };
  }

  BasariLensAddons.EVENTS = EVENTS;
  BasariLensAddons.LOADING_STATUS = LOADING_STATUS;
  BasariLensAddons.ADDON_IDS = ADDON_IDS;
  BasariLensAddons.ROUTE_ID = ROUTE_ID;
  BasariLensAddons.createAccessibilityState = createAccessibilityState;
  BasariLensAddons.createRecommendationState = createRecommendationState;
  BasariLensAddons.createLensAddonsState = createLensAddonsState;
  BasariLensAddons.cloneLensAddonsState = cloneLensAddonsState;
  BasariLensAddons.mergeLensAddonsState = mergeLensAddonsState;
  BasariLensAddons.createDefaultConfiguration = createDefaultConfiguration;
  BasariLensAddons.LensAddonsService = LensAddonsService;
  BasariLensAddons.LensAddonsController = LensAddonsController;
  BasariLensAddons.createFramework = createFramework;

  global.BasariLensAddons = BasariLensAddons;
})(typeof window !== 'undefined' ? window : this);
